import { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useLocale } from "@/context/LocaleContext";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import {
  LayoutDashboard, Lightbulb, Linkedin, Facebook, Mail, Newspaper, Megaphone,
  FolderOpen, Settings, LogOut, Menu, X, Shield, Sparkles, CreditCard, Brain, Feather, Magnet, Lock, Coins, CalendarCheck, MessageSquareQuote, ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import AssistantWidget from "@/components/AssistantWidget";

const NAV = [
  { to: "/idebank", label: "Idégenerator", icon: Lightbulb, testid: "nav-idebank", freeBadge: true },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/ugens-forslag", label: "Ugens forslag", icon: CalendarCheck, testid: "nav-weekly" },
  { to: "/linkedin", label: "LinkedIn-opslag", icon: Linkedin, testid: "nav-linkedin" },
  { to: "/facebook", label: "Facebook-opslag", icon: Facebook, testid: "nav-facebook" },
  { to: "/debatindlaeg", label: "Debatindlæg", icon: MessageSquareQuote, testid: "nav-debate", min: "start" },
  { to: "/nyhedsbrev", label: "Nyhedsbrev", icon: Mail, testid: "nav-newsletter", min: "start" },
  { to: "/presse", label: "Presse", icon: Newspaper, testid: "nav-press", min: "start" },
  { to: "/kampagner", label: "Kampagner", icon: Megaphone, testid: "nav-campaigns", min: "pro" },
  { to: "/leadgenerator", label: "Leadgenerator", icon: Magnet, testid: "nav-lead", min: "pro" },
  { to: "/brandfortaelling", label: "Brandfortælling", icon: Feather, testid: "nav-brand", min: "pro" },
  { to: "/vidensbank", label: "Vidensbank", icon: Brain, testid: "nav-knowledge", min: "start" },
  { to: "/projekter", label: "Gemte projekter", icon: FolderOpen, testid: "nav-projects" },
  { to: "/indstillinger", label: "Indstillinger", icon: Settings, testid: "nav-settings" },
];

const PLAN_RANK = { gratis: 0, start: 1, pro: 2, business: 2 };
const PLAN_LABELS = { gratis: "Gratis", start: "Start", pro: "Pro", business: "Business" };

function SidebarContent({ onNavigate }) {
  const { user, company, logout } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();
  const [knowledge, setKnowledge] = useState(null);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (user?.onboarded && user?.role !== "admin") {
      const canKnowledge = PLAN_RANK[user?.plan || "gratis"] >= PLAN_RANK["start"];
      const refreshUsage = () => api.get("/usage").then(({ data }) => setUsage(data)).catch(() => {});
      if (canKnowledge) {
        api.get("/knowledge").then(({ data }) => setKnowledge(data.percent)).catch(() => {});
      } else {
        setKnowledge(null);
      }
      refreshUsage();
      window.addEventListener("usage-changed", refreshUsage);
      return () => window.removeEventListener("usage-changed", refreshUsage);
    }
  }, [user]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-full flex-col">
      <div className="px-6 py-6 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-foreground flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-background" strokeWidth={2} />
          </div>
          <span className="font-display font-bold tracking-tight text-foreground">Aicom<span className="text-muted-foreground">AI</span></span>
        </div>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-0.5 overflow-y-auto">
        {NAV.map((item) => {
          const locked = user?.role !== "admin" && item.min && PLAN_RANK[user?.plan || "gratis"] < PLAN_RANK[item.min];
          if (locked) {
            return (
              <button
                key={item.to}
                data-testid={`${item.testid}-locked`}
                onClick={() => {
                  toast.info(t('Opgradér til {plan} for at låse "{label}" op', { plan: t(PLAN_LABELS[item.min]), label: t(item.label) }));
                  navigate("/priser");
                  onNavigate?.();
                }}
                className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 relative"
                title={t("Kræver {plan}-planen", { plan: t(PLAN_LABELS[item.min]) })}
              >
                <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                <span>{t(item.label)}</span>
                <Lock className="h-3.5 w-3.5 ml-auto shrink-0" strokeWidth={2} />
              </button>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              data-testid={item.testid}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-200 relative ${
                  isActive ? "text-foreground bg-secondary" : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              <item.icon className="h-4 w-4" strokeWidth={1.75} />
              {t(item.label)}
              {item.freeBadge && user?.role !== "admin" && (user?.plan || "gratis") === "gratis" && (
                <span className="ml-auto rounded-full bg-[#22D3C5]/15 text-[#0F766E] border border-[#22D3C5]/40 px-1.5 text-[9px] leading-4 font-medium uppercase tracking-wider" data-testid="nav-idebank-free-badge">
                  {t("Gratis")}
                </span>
              )}
            </NavLink>
          );
        })}
        {user?.role === "admin" && (
          <NavLink to="/admin" onClick={onNavigate} data-testid="nav-admin"
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-200 ${isActive ? "text-foreground bg-secondary" : "text-muted-foreground hover:text-foreground"}`}>
            <Shield className="h-4 w-4" strokeWidth={1.75} /> {t("Administrator")}
          </NavLink>
        )}
      </nav>

      <div className="px-3 pb-4 pt-2 border-t border-border space-y-2">
        {user?.is_owner && (
        <button onClick={() => { navigate("/priser"); onNavigate?.(); }} data-testid="nav-upgrade"
          className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-border hover:border-muted-foreground/50 transition-colors duration-200">
          <span className="flex items-center gap-2 text-sm text-foreground"><CreditCard className="h-4 w-4" strokeWidth={1.75} /> {t(PLAN_LABELS[user?.plan] || "Gratis")}</span>
          <span className="text-xs text-muted-foreground">{t("Opgradér")}</span>
        </button>
        )}
        {usage && (
          <div data-testid="credits-indicator" className="flex items-center gap-2 px-3 py-1.5 text-xs">
            <Coins className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
            {usage.unlimited ? (
              <span className="text-muted-foreground">{t("Ubegrænset brug")}</span>
            ) : (
              <span className={usage.remaining <= 3 ? "text-foreground font-medium" : "text-muted-foreground"}>
                {t(usage.remaining === 1 ? "{n} kredit tilbage" : "{n} kreditter tilbage", { n: usage.remaining })}
              </span>
            )}
          </div>
        )}
        <div className="px-3 py-2">
          <p className="text-sm text-foreground truncate">{user?.name || company?.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        <button onClick={doLogout} data-testid="logout-button"
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:border-muted-foreground/50 transition-colors duration-200">
          <LogOut className="h-4 w-4 shrink-0" strokeWidth={1.75} /> {t("Log ud")}
        </button>
        {knowledge !== null && (
          <button onClick={() => { navigate("/vidensbank"); onNavigate?.(); }} data-testid="knowledge-indicator"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors duration-200">
            <Brain className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{t("AI har lært {n} % af din virksomhed", { n: knowledge })}</span>
          </button>
        )}
      </div>
    </div>
  );
}

export default function AppLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isLight = theme !== "dark";
  const panelBg = isLight ? "bg-secondary" : "bg-[#050505]";

  useEffect(() => {
    const b = document.body;
    if (isLight) b.classList.remove("dark");
    else b.classList.add("dark");
    return () => b.classList.remove("dark");
  }, [isLight]);

  return (
    <div className={`min-h-screen bg-background ${isLight ? "" : "dark"}`}>
      <div className="grain" />
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex fixed inset-y-0 left-0 w-64 border-r border-border ${panelBg} z-30`}>
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <div className={`md:hidden sticky top-0 z-40 flex items-center justify-between px-4 h-14 border-b border-border ${isLight ? "bg-background/80" : "bg-black/70"} backdrop-blur-xl`}>
        <div className="flex items-center gap-1">
          {location.pathname !== "/dashboard" && (
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="mobile-back-button" aria-label="Tilbage" className="-ml-2">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <span className="font-display font-bold">Aicom<span className="text-muted-foreground">AI</span></span>
        </div>
        <div className="flex items-center gap-2">
          <LocaleSwitcher />
          <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {open && (
        <div className={`md:hidden fixed inset-0 z-50 ${isLight ? "" : "dark"}`}>
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className={`absolute inset-y-0 left-0 w-72 ${panelBg} border-r border-border flex flex-col`}>
            <div className="flex justify-end p-3 shrink-0">
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)}><X className="h-5 w-5" /></Button>
            </div>
            <div className="flex-1 min-h-0">
              <SidebarContent onNavigate={() => setOpen(false)} />
            </div>
          </div>
        </div>
      )}

      <main className="md:pl-64 relative z-10">
        {/* Desktop top bar with language switcher (top-right) */}
        <div className={`hidden md:flex sticky top-0 z-20 items-center justify-end h-14 px-6 md:px-12 border-b border-border ${isLight ? "bg-background/80" : "bg-black/60"} backdrop-blur-xl`} data-testid="app-topbar">
          <LocaleSwitcher />
        </div>
        <div className="max-w-6xl mx-auto p-6 sm:p-8 md:p-12" key={location.pathname} data-clarity-mask="true">
          <Outlet />
        </div>
      </main>

      <AssistantWidget />
    </div>
  );
}
